import { useState, useEffect, useContext, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api as Api } from 'api/http';
import { CompanyContext } from 'contexts/CompanyContext';

const FOLLOWR_PROMPTS_KEY = ['prompts', 'legacyFollowr'];
const companyPromptsKey = (companyId) => [
    'prompts',
    'legacyCompany',
    String(companyId ?? ''),
];

async function fetchAllPromptsPages(companyId) {
    const filterStr =
        companyId == null
            ? 'filter[company_id]=null'
            : `filter[company_id]=${companyId}`;
    const initial = await Api.get(
        `/prompts?include=user,user.image.thumbnail&sort=-created_at&${filterStr}&page[size]=30`
    );
    let acc = [...initial.data];
    const totalPages = Math.ceil(
        initial.meta.total / initial.meta.per_page
    );
    if (totalPages > 1) {
        const calls = [];
        for (let page = 2; page <= totalPages; page++) {
            calls.push(
                Api.get(
                    `/prompts?include=user,user.image.thumbnail&sort=-created_at&${filterStr}&page[number]=${page}&page[size]=30`
                )
            );
        }
        const responses = await Promise.all(calls);
        responses.forEach((r) => {
            acc = [...acc, ...r.data];
        });
    }
    return acc;
}

const usePrompts = () => {
    const { company } = useContext(CompanyContext);
    const qc = useQueryClient();
    const [followrPrompts, setFollowrPrompts] = useState([]);
    const [companyPrompts, setCompanyPrompts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchFollowrPrompts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const all = await qc.fetchQuery({
                queryKey: FOLLOWR_PROMPTS_KEY,
                staleTime: 5 * 60 * 1000,
                queryFn: () => fetchAllPromptsPages(null),
            });
            setFollowrPrompts(all);
        } catch (error) {
            console.error('Error fetching followr prompts:', error);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [qc]);

    const fetchCompanyPrompts = useCallback(async () => {
        if (!company?.id) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const all = await qc.fetchQuery({
                queryKey: companyPromptsKey(company.id),
                staleTime: 60_000,
                queryFn: () => fetchAllPromptsPages(company.id),
            });
            setCompanyPrompts(all);
        } catch (error) {
            console.error('Error fetching company prompts:', error);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [company?.id, qc]);

    const createPrompt = async (network, name = null, prompt = null) => {
        try {
            const response = await Api.post('/prompts', {}, {
                name: name || `Prompt Title`,
                prompt: prompt || `Write a...`,
                company_id: company?.id,
                type: 'text',
                social_network_type: network,
                default: false
            });

            const newPrompt = {
                ...response.data,
                isNew: !name && !prompt
            };

            setCompanyPrompts(prev => [...prev, newPrompt]);
            qc.invalidateQueries({ queryKey: ['prompts'] });
            return newPrompt;
        } catch (error) {
            console.error('Error creating prompt:', error);
            throw error;
        }
    };

    const deletePrompt = async (prompt) => {
        try {
            await Api.delete(`/prompts/${prompt.id}`);
            qc.invalidateQueries({ queryKey: ['prompts'] });
            await fetchCompanyPrompts();
        } catch (error) {
            console.error('Error deleting prompt:', error);
            throw error;
        }
    };

    const updatePrompt = async (promptId, promptData) => {
        try {
            await Api.put(`/prompts/${promptId}`, {}, promptData);
            qc.invalidateQueries({ queryKey: ['prompts'] });
            await fetchCompanyPrompts();
        } catch (error) {
            console.error('Error updating prompt:', error);
            throw error;
        }
    };

    const getPromptForNetwork = (network, query = "") => {
        let configs = "";
        let querySuffix = query 
            ? `You must write about ${query}. ` 
            : company && company?.interests?.length
                ? `You must write about ${company?.interests[Math.floor(Math.random() * company?.interests?.length)]}. `
                : ``;
        // socialMediaPrompts[network]['configs'].map((config) => {
        //     if (randomBool()) {
        //         configs += config;
        //     }
        // });
        let finalPrompt = prompt + querySuffix + configs;
        return finalPrompt
    }

    const getDefaultPromptsForNetwork = async (network) => {
        await fetchFollowrPrompts();
        await fetchCompanyPrompts();
        
        const networkPrompts = [
            ...(followrPrompts || []),
            ...(companyPrompts || [])
        ];
        
        return networkPrompts.filter(p => p.default && p.social_network_type === network);
    };

    const getRandomPromptForNetwork = (network) => {
        const defaultPrompts = [...followrPrompts, ...companyPrompts]
            .filter(p => p.default && p.social_network_type === network);
        
        if (defaultPrompts.length > 0) {
            return defaultPrompts[Math.floor(Math.random() * defaultPrompts.length)];
        }

        const allPrompts = [...followrPrompts, ...companyPrompts]
            .filter(p => p.social_network_type === network);
            
        return allPrompts.length > 0 
            ? allPrompts[Math.floor(Math.random() * allPrompts.length)]
            : null;
    };

    return {
        followrPrompts,
        companyPrompts,
        prompts: [...followrPrompts, ...companyPrompts],
        loading,
        error,
        fetchFollowrPrompts,
        fetchCompanyPrompts,
        createPrompt,
        deletePrompt,
        updatePrompt,
        getDefaultPromptsForNetwork,
        getRandomPromptForNetwork,
    };
};

export default usePrompts;